import {
  buildConversationApiUrl,
  environment,
  parseSummarizeJobName,
  parseTranscribeJobName,
} from "../../config/environment";
import { isCapabilityEnabled } from "../../config/capabilities";
import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../Sidebar";
import { customFetch } from "../../utils/Auth";
import {
  fetchSummarizeJob,
  fetchTranscribeJob,
} from "../../utils/secure-job-api";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import PatientSummaryTable from "./PatientTable";
import DoctorInfo from "./DoctorInfo";
import PatientInfo from "./PatientInfo";
import ProcessingJobsTable from "./ProcessingJobsTable";
import CopyProcessingPatientIdsMenu from "./CopyProcessingPatientIdsMenu";
import FailedJobsTable from "./FailedJobsTable";
import TriggerLambda from "./TriggerLambda";
import LLMSelection from "./LLMSelection";
import ClientLogs from "./ClientLogs";
import NotificationsPage from "../Notifications/NotificationsPage";
import FlowLine from "./FlowLine";
import { fetchInsightsByPatient } from "../Insights";

const SEARCH_METHODS = [
  { key: 'patient', label: 'Patient' },
  { key: 'doctor', label: 'Doctor' },
  { key: 'processing', label: 'Processing Jobs' },
  { key: 'failed', label: 'Failed Jobs' },
  { key: 'trigger', label: 'Trigger Lambda' },
  { key: 'llm-selection', label: 'LLM Selection' },
  { key: 'client-events', label: 'Client Events' },
  ...(isCapabilityEnabled("notifications")
    ? [{ key: 'notifications', label: 'Notifications' }]
    : []),
  { key: 'flow-line', label: 'Flow Line' },
];

const Review = ({ setAuth, setUserOrg }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMethod, setSearchMethod] = useState('patient');
  const [jobName, setJobName] = useState("");
  const [jobResults, setJobResults] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
  const [mrn, setMrn] = useState("");
  const [appointmentId, setAppointmentId] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [doctorSearch, setDoctorSearch] = useState("");
  const [allDoctors, setAllDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showFullPatientDetails, setShowFullPatientDetails] = useState(false);
  const [isEditPatientMode, setIsEditPatientMode] = useState(false);
  const [showPatientUpdateMsg, setShowPatientUpdateMsg] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [processingJobs, setProcessingJobs] = useState(null);
  const [isLoadingProcessingJobs, setIsLoadingProcessingJobs] = useState(false);
  const [failedJobs, setFailedJobs] = useState(null);
  const [isLoadingFailedJobs, setIsLoadingFailedJobs] = useState(false);
  const [triggerLambda, setTriggerLambda] = useState(null);
  const [isLoadingTriggerLambda, setIsLoadingTriggerLambda] = useState(false);

  // Handle URL parameter changes for tab navigation
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      // Validate that the tab exists in SEARCH_METHODS
      const validTab = SEARCH_METHODS.find(method => method.key === tabFromUrl);
      if (validTab) {
        setSearchMethod(tabFromUrl);
      } else {
        // Invalid tab, default to patient and update URL
        setSearchMethod('patient');
        setSearchParams({ tab: 'patient' });
      }
    } else {
      // No tab parameter, default to patient and update URL
      setSearchMethod('patient');
      setSearchParams({ tab: 'patient' });
    }
  }, [searchParams, setSearchParams]);

  // When patientData changes, set selectedPatient and selectedPatientId for all tabs if possible
  useEffect(() => {
    if (patientData && !Array.isArray(patientData) && Object.keys(patientData).length > 0) {
      setSelectedPatient(patientData);
      setSelectedPatientId(patientData.patient_id || null);
    } else if (Array.isArray(patientData) && patientData.length === 1) {
      setSelectedPatient(patientData[0]);
      setSelectedPatientId(patientData[0].patient_id || null);
    } else if (Array.isArray(patientData) && patientData.length > 1) {
      const isListed = (id) =>
        id != null && patientData.some((row) => String(row.patient_id) === String(id));
      setSelectedPatientId((currentId) => (isListed(currentId) ? currentId : null));
      setSelectedPatient((current) => (isListed(current?.patient_id) ? current : null));
    } else {
      setSelectedPatient(null);
      setSelectedPatientId(null);
    }
  }, [patientData]);

  async function getPatientData(id) {
    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/id/${id}`,
        {
          method: "GET",
        }
      );
      const parseRes = await response.json();
      return parseRes;
    } catch (err) {
      return { error: "Failed to fetch patient data" };
    }
  }

  async function refreshPatientAfterDeletionChange(patientId) {
    const patient = await getPatientData(patientId);
    const isVisible = patient && !patient.error && Object.keys(patient).length > 0;
    const isSamePatient = (row) => String(row?.patient_id) === String(patientId);

    setPatientData((current) => {
      if (Array.isArray(current)) {
        return isVisible
          ? current.map((row) => (isSamePatient(row) ? { ...row, is_deleted: patient.is_deleted } : row))
          : current.filter((row) => !isSamePatient(row));
      }
      return isVisible ? patient : null;
    });
    if (isVisible) {
      setSelectedPatient((current) => (isSamePatient(current) ? patient : current));
    }
  }

  async function getPatientsByMrn(mrn) {
    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/summary/mrn/${mrn}`,
        { method: "GET" }
      );
      const parseRes = await response.json();
      return Array.isArray(parseRes) ? parseRes : [];
    } catch (err) {
      return [];
    }
  }

  async function getPatientsByAppointmentId(appointmentId) {
    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/summary/appointment-id/${appointmentId}`,
        { method: "GET" }
      );
      const parseRes = await response.json();
      return Array.isArray(parseRes) ? parseRes : [];
    } catch (err) {
      return [];
    }
  }

  const fetchPatient = async (event) => {
    event.preventDefault();
    
    // Determine which field has a value and search accordingly
    if (jobName.trim()) {
      // Search by Patient ID / Job Name
      if (!jobName.trim()) {
        alert("Please enter a job name or patient ID");
        return;
      }
      setIsLoading(true);
      setJobResults(null);
      setPatientData(null);

      // 1. If input is a job
      if (parseSummarizeJobName(jobName) !== null) {
        try {
          const data = await fetchSummarizeJob(jobName);
          setJobResults(data);
          if (data && data.patient_id) {
            setIsLoadingPatientData(true);
            try {
              const patient = await getPatientData(data.patient_id);
              setPatientData(patient);
            } catch (error) {
              setPatientData({ error: "Failed to fetch patient data" });
            } finally {
              setIsLoadingPatientData(false);
            }
          }
        } catch (error) {
          setJobResults({ error: "Failed to fetch summarize job information" });
        } finally {
          setIsLoading(false);
        }
        return;
      }
      if (parseTranscribeJobName(jobName) !== null) {
        try {
          const data = await fetchTranscribeJob(jobName);
          setJobResults(data);
          if (data && data.patient_id) {
            setIsLoadingPatientData(true);
            try {
              const patient = await getPatientData(data.patient_id);
              setPatientData(patient);
            } catch (error) {
              setPatientData({ error: "Failed to fetch patient data" });
            } finally {
              setIsLoadingPatientData(false);
            }
          }
        } catch (error) {
          setJobResults({ error: "Failed to fetch transcribe job information" });
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // 2. If input is all digits, treat as patient ID
      if (/^\d+$/.test(jobName.trim())) {
        setIsLoadingPatientData(true);
        try {
          const patient = await getPatientData(jobName.trim());
          if (patient && !patient.error && Object.keys(patient).length > 0) {
            setPatientData(patient);
          } else {
            setPatientData({ error: "Patient not found" });
          }
        } catch (error) {
          setPatientData({ error: "Failed to fetch patient data" });
        } finally {
          setIsLoading(false);
          setIsLoadingPatientData(false);
        }
        return;
      }

      // 3. Otherwise, show error
      setIsLoading(false);
      setPatientData({ error: "Input must be a Summarize/Transcribe job name or a numeric Patient ID." });
    } else if (mrn.trim()) {
      // Search by MRN
      setIsLoadingPatientData(true);
      setPatientData(null);
      try {
        const patients = await getPatientsByMrn(mrn.trim());
        setPatientData(patients);
      } finally {
        setIsLoadingPatientData(false);
      }
    } else if (appointmentId.trim()) {
      // Search by Appointment ID
      setIsLoadingPatientData(true);
      setPatientData(null);
      try {
        const patients = await getPatientsByAppointmentId(appointmentId.trim());
        setPatientData(patients);
      } finally {
        setIsLoadingPatientData(false);
      }
    } else {
      alert("Please enter at least one search criteria");
    }
  };

  const fetchProcessingJobs = useCallback(async () => {
    setIsLoadingProcessingJobs(true);
    setProcessingJobs(null);
    try {
      const response = await customFetch(
        `${environment.apiUrl}/doctors/doctors-with-processing-notes`,
        { method: "GET" }
      );
      const data = await response.json();
      setProcessingJobs(data);
    } catch (error) {
      console.error('Failed to fetch processing jobs:', error);
      setProcessingJobs([]);
    } finally {
      setIsLoadingProcessingJobs(false);
    }
  }, []);


  // Fetch processing jobs when processing tab is selected
  useEffect(() => {
    if (searchMethod === 'processing') {
      fetchProcessingJobs();
    }
  }, [searchMethod, fetchProcessingJobs]);

  async function fetchTrace(patientId) {
    if (!patientId) return;
    try {
      const result = await fetchInsightsByPatient(patientId);
      if (result.error) throw new Error(result.error);
      setTraceData(result.data);
    } catch (err) {
      setTraceData({ error: "Failed to fetch insights data" });
    }
  }

  async function fetchJobsByPatient(patientId) {
    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/transcribe-jobs/by-patient/${patientId}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) throw new Error('Failed to fetch jobs');
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        const jobNames = data.map(job => job.transcribe_job_name).filter(Boolean);
        console.log('Job names for patient', patientId, ':', jobNames);
        
        // Extract conversation ID from the first job name
        if (jobNames.length > 0) {
          const firstJobName = jobNames[0];
          const conversationId = parseTranscribeJobName(firstJobName, {
            stripMp3: true,
          });
          if (conversationId && firstJobName.endsWith(".mp3")) {
            console.log('Extracted conversation ID:', conversationId);
            
            // Make POST request to conversation endpoint
            try {
              const conversationResponse = await fetch(buildConversationApiUrl(), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  conversation_id: conversationId
                })
              });
              
              if (conversationResponse.ok) {
                const conversationData = await conversationResponse.json();
                console.log('Conversation response:', conversationData);
                alert('Re-upload initiated successfully!');
              } else {
                console.error('Failed to initiate re-upload:', conversationResponse.status);
                alert('Failed to initiate re-upload. Please try again.');
              }
            } catch (conversationErr) {
              console.error('Error making conversation request:', conversationErr);
              alert('Error initiating re-upload. Please try again.');
            }
          } else {
            console.error('Could not extract conversation ID from job name:', firstJobName);
            alert('Could not extract conversation ID from job name.');
          }
        } else {
          console.log('No jobs found for patient', patientId);
          alert('No jobs found for this patient.');
        }
      } else {
        console.log('No jobs found for patient', patientId);
        alert('No jobs found for this patient.');
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      alert('Failed to fetch jobs for this patient.');
    }
  }

  return (
    <div className="flex">
      <div className="h-[100dvh]">
        <div className="flex h-[100%] fixed top-0">
          <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
        </div>
      </div>
      <div
        className="pl-[10px] pr-[10px] flex flex-col w-full overflow-x-hidden"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="flex flex-col w-full">
          <div className="flex flex-col justify-center items-center w-full">
            <h1 className="font-bold text-[28px] text-ktx-purple mt-[2rem]">
              Oncall
            </h1>
            {/* Search Method Tabs */}
            <div className="mt-8 w-full flex border-b border-gray-200">
              {SEARCH_METHODS.map(method => (
                <button
                  key={method.key}
                  className={`px-4 py-2 text-sm font-medium focus:outline-none transition-colors border-b-2 ${searchMethod === method.key ? 'border-ktx-purple text-ktx-purple' : 'border-transparent text-gray-600 hover:text-ktx-purple'}`}
                  onClick={() => {
                    setSearchMethod(method.key);
                    setSearchParams({ tab: method.key });
                    setPatientData(null);
                    setSelectedPatient(null);
                    setSelectedPatientId(null);
                    // Clear search fields when switching to patient tab
                    if (method.key === 'patient') {
                      setJobName("");
                      setMrn("");
                      setAppointmentId("");
                    }
                    if (method.key !== 'processing') {
                      setProcessingJobs(null);
                    }
                    if (method.key !== 'failed') {
                      setFailedJobs(null);
                    }
                  }}
                  type="button"
                >
                  {method.label}
                </button>
              ))}
            </div>
            {/* Search Forms */}
            <div className="w-full mt-6">
              {searchMethod === 'patient' && (
                <form onSubmit={fetchPatient} className="flex flex-col space-y-6 items-start">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                    <div>
                      <label
                        htmlFor="jobName"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Patient ID / Job Name
                      </label>
                      <Input
                        type="text"
                        id="jobName"
                        value={jobName}
                        onChange={(e) => {
                          setJobName(e.target.value);
                          // Clear other fields when this one is used
                          if (e.target.value.trim()) {
                            setMrn("");
                            setAppointmentId("");
                          }
                        }}
                        placeholder="Enter Patient ID or Job Name..."
                      />
                    </div>
                    <div>
                      <label htmlFor="mrn" className="block text-sm font-medium text-gray-700 mb-2">
                        MRN
                      </label>
                      <Input
                        type="text"
                        id="mrn"
                        value={mrn}
                        onChange={(e) => {
                          setMrn(e.target.value);
                          // Clear other fields when this one is used
                          if (e.target.value.trim()) {
                            setJobName("");
                            setAppointmentId("");
                          }
                        }}
                        placeholder="Enter MRN..."
                      />
                    </div>
                    <div>
                      <label htmlFor="appointmentId" className="block text-sm font-medium text-gray-700 mb-2">
                        Appointment ID
                      </label>
                      <Input
                        type="text"
                        id="appointmentId"
                        value={appointmentId}
                        onChange={(e) => {
                          setAppointmentId(e.target.value);
                          // Clear other fields when this one is used
                          if (e.target.value.trim()) {
                            setJobName("");
                            setMrn("");
                          }
                        }}
                        placeholder="Enter Appointment ID..."
                      />
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Enter any one of the above fields to search for patients
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading || isLoadingPatientData}
                    className="bg-ktx-purple hover:bg-purple-700"
                  >
                    {(isLoading || isLoadingPatientData) ? "Searching..." : "Search Patients"}
                  </Button>
                </form>
              )}
              {searchMethod === 'doctor' && (
                <DoctorInfo
                  selectedDoctor={selectedDoctor}
                  setSelectedDoctor={setSelectedDoctor}
                  doctorSearch={doctorSearch}
                  setDoctorSearch={setDoctorSearch}
                  allDoctors={allDoctors}
                  setAllDoctors={setAllDoctors}
                  filteredDoctors={filteredDoctors}
                  setFilteredDoctors={setFilteredDoctors}
                  selectedPatient={selectedPatient}
                  setSelectedPatient={setSelectedPatient}
                  selectedPatientId={selectedPatientId}
                  setSelectedPatientId={setSelectedPatientId}
                  setIsLoadingPatientData={setIsLoadingPatientData}
                  getPatientData={getPatientData}
                  showTrace={showTrace}
                  setShowTrace={setShowTrace}
                  traceData={traceData}
                  setTraceData={setTraceData}
                  fetchTrace={fetchTrace}
                  fetchJobsByPatient={fetchJobsByPatient}
                />
              )}
              {searchMethod === 'processing' && (
                <div className="w-full">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Processing Jobs ({processingJobs?.length ?? 0})
                    </h2>
                    <div className="flex items-center gap-2">
                      <CopyProcessingPatientIdsMenu
                        processingJobs={processingJobs}
                        disabled={isLoadingProcessingJobs}
                      />
                      <Button
                        onClick={fetchProcessingJobs}
                        disabled={isLoadingProcessingJobs}
                        className="bg-ktx-purple hover:bg-purple-700"
                      >
                        {isLoadingProcessingJobs ? "Refreshing..." : "Refresh"}
                      </Button>
                    </div>
                  </div>
                  <ProcessingJobsTable 
                    processingJobs={processingJobs} 
                    isLoading={isLoadingProcessingJobs}
                    onRefresh={fetchProcessingJobs}
                  />
                </div>
              )}
              {searchMethod === 'failed' && (
                <div className="w-full">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Failed Jobs
                    </h2>
                  </div>
                  <FailedJobsTable 
                    failedJobs={failedJobs} 
                    isLoading={isLoadingFailedJobs}
                  />
                </div>
              )}
              {searchMethod === 'trigger' && (
                <div className="w-full">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                    </h2>
                  </div>
                  <TriggerLambda />
                </div>
              )}
              {searchMethod === 'llm-selection' && (
                <div className="w-full">
                  <LLMSelection />
                </div>
              )}
              {searchMethod === 'client-events' && (
                <div className="w-full">
                  <ClientLogs 
                    allDoctors={allDoctors}
                    setAllDoctors={setAllDoctors}
                  />
                </div>
              )}
              {searchMethod === 'notifications' && (
                <div className="w-full">
                  <NotificationsPage />
                </div>
              )}
              {searchMethod === 'flow-line' && (
                <div className="w-full">
                  <FlowLine />
                </div>
              )}
            </div>

            {/* Patient Data Display */}
            {isLoadingPatientData && (
              <div className="mt-8 w-full">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Patient Data
                </h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-600">Loading patient data...</p>
                </div>
              </div>
            )}
            {patientData && !isLoadingPatientData && (
              <div className="mt-8 w-full">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Patient Data
                </h2>
                <PatientSummaryTable
                  patients={patientData}
                  onPatientIdClick={async (id) => {
                    setIsLoadingPatientData(true);
                    setSelectedPatientId(id);
                    setSelectedPatient(null);
                    try {
                      const patient = await getPatientData(id);
                      setSelectedPatient(patient);
                      setSelectedPatientId(patient.patient_id || null);
                    } finally {
                      setIsLoadingPatientData(false);
                    }
                  }}
                  selectedPatientId={selectedPatientId}
                  onDeletionChange={refreshPatientAfterDeletionChange}
                />
                {/* Patient details card for selected patient in all tabs */}
                <PatientInfo
                  selectedPatient={selectedPatient}
                  setSelectedPatient={setSelectedPatient}
                  showFullPatientDetails={showFullPatientDetails}
                  setShowFullPatientDetails={setShowFullPatientDetails}
                  isEditPatientMode={isEditPatientMode}
                  setIsEditPatientMode={setIsEditPatientMode}
                  showPatientUpdateMsg={showPatientUpdateMsg}
                  setShowPatientUpdateMsg={setShowPatientUpdateMsg}
                  showTrace={showTrace}
                  setShowTrace={setShowTrace}
                  traceData={traceData}
                  fetchTrace={fetchTrace}
                  fetchJobsByPatient={fetchJobsByPatient}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Review;
